import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import {
  CheckIcon,
  ChevronRightIcon,
  CircleStackIcon
} from "@heroicons/react/24/outline";
import { cn } from "../private/utils.js";
import { useImperativeHandle, useRef, useState } from "react";
import { useIsGummy } from "../hooks/useGummyTheme.js";

const GUMMY_CONTENT = cn(
  "rounded-surface-md p-2",
  "bg-tile-popover text-mono-900 dark:text-mono-100",
  "shadow-card-deep"
);

const LEGACY_CONTENT = cn(
  "dark:bg-mono-950 dark:shadow-none dark:border-mono-700/50",
  "rounded-md border bg-mono-50 p-1 text-mono-700 shadow-lg dark:text-mono-200"
);

const GUMMY_ITEM = cn(
  "rounded-surface-2xs min-h-[56px] gap-3 px-4 py-3 text-base font-black",
  "focus:bg-mono-100 dark:focus:bg-mono-600",
  "hover:bg-mono-100 dark:hover:bg-mono-600"
);

const LEGACY_ITEM = cn(
  "rounded-xs px-2 py-1.5 text-sm",
  "focus:bg-mono-200/50 focus:text-accent-foreground dark:focus:bg-mono-800/50"
);

const DropdownMenuContext = React.createContext<{
  isOpen: boolean;
  changeMenuState: (state: "open" | "close") => void;
}>({
  isOpen: false,
  changeMenuState: () => {}
});

const useDropdownMenu = () => {
  const context = React.useContext(DropdownMenuContext);
  if (context === undefined) {
    throw new Error(
      "useDropdownMenu must be used within a DropdownMenuContext"
    );
  }
  return context;
};

const Menu = ({
  children,
  modal = true,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root> & {
  modal?: boolean;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const changeMenuState = (state: "open" | "close") => {
    setIsOpen(state === "open");
  };
  return (
    <DropdownMenuContext.Provider value={{ isOpen, changeMenuState }}>
      <DropdownMenuPrimitive.Root
        modal={modal}
        onOpenChange={setIsOpen}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Root>
    </DropdownMenuContext.Provider>
  );
};

const withRoot =
  (Component: () => React.JSX.Element) =>
  ({
    ...props
  }: React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root>) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const changeMenuState = (state: "open" | "close") => {
      setIsOpen(state === "open");
    };
    return (
      <DropdownMenuContext.Provider value={{ isOpen, changeMenuState }}>
        <DropdownMenuPrimitive.Root onOpenChange={setIsOpen} {...props}>
          <Component />
        </DropdownMenuPrimitive.Root>
      </DropdownMenuContext.Provider>
    );
  };

const GUMMY_TRIGGER = cn(
  "inline-flex h-12 w-12 items-center justify-center rounded-full",
  "bg-transparent text-mono-700 dark:text-mono-200",
  "transition-[background-color,color] duration-150",
  "hover:bg-mono-200/60 dark:hover:bg-mono-800/60",
  "data-[state=open]:bg-mono-200/70 dark:data-[state=open]:bg-mono-800/70",
  "motion-reduce:transition-none",
  "outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40"
);

const Trigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger>
>(({ className, asChild, ...props }, ref) => {
  const isGummy = useIsGummy();
  if (asChild) {
    return (
      <DropdownMenuPrimitive.Trigger
        ref={ref}
        asChild
        className={className}
        {...props}
      />
    );
  }
  return (
    <DropdownMenuPrimitive.Trigger
      ref={ref}
      className={cn(isGummy && GUMMY_TRIGGER, className)}
      {...props}
    />
  );
});
Trigger.displayName = DropdownMenuPrimitive.Trigger.displayName;

const Group = DropdownMenuPrimitive.Group;

const Portal = DropdownMenuPrimitive.Portal;

const Sub = DropdownMenuPrimitive.Sub;

const RadioGroup = DropdownMenuPrimitive.RadioGroup;

const SubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ className, inset, children, ...props }, ref) => {
  const isGummy = useIsGummy();
  return (
    <DropdownMenuPrimitive.SubTrigger
      ref={ref}
      className={cn(
        "flex cursor-default select-none items-center outline-hidden",
        isGummy
          ? cn(
              GUMMY_ITEM,
              "data-[state=open]:bg-mono-100 dark:data-[state=open]:bg-mono-700/60"
            )
          : cn(LEGACY_ITEM, "focus:bg-accent data-[state=open]:bg-accent"),
        inset && "pl-8",
        className
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto h-5 w-5" strokeWidth={2.5} />
    </DropdownMenuPrimitive.SubTrigger>
  );
});
SubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;

const adjustSubmenuPosition = (
  ref: React.RefObject<HTMLInputElement | null>
) => {
  if (!ref) return 0;
  const submenu = ref.current;
  if (submenu) {
    const available = Number(
      getComputedStyle(submenu)
        .getPropertyValue("--radix-popper-available-width")
        ?.replace(`px`, ``) ?? 0
    );
    const rect = submenu.getBoundingClientRect();
    if (rect.width <= available) return 0;
    return available - rect.width;
  }
  return 0;
};

const SubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, outerRef) => {
  const innerRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(outerRef, () => innerRef.current!, []);
  const [sideOffset, setSideOffset] = useState(0);
  const isGummy = useIsGummy();

  return (
    <DropdownMenuPrimitive.SubContent
      onAnimationStartCapture={(_props) => {
        const offset = adjustSubmenuPosition(innerRef);
        if (offset !== sideOffset) {
          setSideOffset(offset);
        }
      }}
      sideOffset={sideOffset}
      ref={innerRef}
      className={cn(
        "z-50 min-w-[8rem] animate-in data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1",
        isGummy ? GUMMY_CONTENT : LEGACY_CONTENT,
        className
      )}
      {...props}
    />
  );
});
SubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

const Content = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
    arrowPadding?: number;
  }
>(
  (
    { className, sideOffset = 4, arrowPadding = 1, onCloseAutoFocus, ...props },
    ref
  ) => {
    const isGummy = useIsGummy();
    return (
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          arrowPadding={arrowPadding}
          loop={false}
          // Prevent auto-focus on open to avoid conflicts with parent focus-managing components (dialogs)
          // @ts-expect-error - onOpenAutoFocus exists at runtime but types are outdated
          onOpenAutoFocus={(e: Event) => e.preventDefault()}
          onCloseAutoFocus={(e) => {
            // Prevent focus return to avoid conflicts with other focus-managing components
            e.preventDefault();
            onCloseAutoFocus?.(e);
          }}
          className={cn(
            "z-50 min-w-[8rem]",
            isGummy ? GUMMY_CONTENT : LEGACY_CONTENT,
            "data-[side=bottom]:origin-top data-[side=left]:origin-right data-[side=right]:origin-left data-[side=top]:origin-bottom",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            className
          )}
          {...props}
        />
      </DropdownMenuPrimitive.Portal>
    );
  }
);

Content.displayName = DropdownMenuPrimitive.Content.displayName;

const Item = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => {
  const isGummy = useIsGummy();
  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={cn(
        "data-disabled:pointer-events-none data-disabled:opacity-50 relative flex cursor-default select-none items-center outline-hidden transition-colors",
        isGummy ? GUMMY_ITEM : LEGACY_ITEM,
        inset && "pl-8",
        className
      )}
      {...props}
    />
  );
});
Item.displayName = DropdownMenuPrimitive.Item.displayName;

const CheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => {
  const isGummy = useIsGummy();
  return (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      className={cn(
        "data-disabled:pointer-events-none data-disabled:opacity-50 relative flex cursor-default select-none items-center outline-hidden transition-colors",
        isGummy
          ? cn(GUMMY_ITEM, "pl-11")
          : cn(
              LEGACY_ITEM,
              "pl-8 focus:bg-accent focus:text-accent-foreground"
            ),
        className
      )}
      checked={checked}
      {...props}
    >
      <span
        className={cn(
          "absolute left-3 flex items-center justify-center",
          isGummy ? "h-5 w-5" : "h-3.5 w-3.5"
        )}
      >
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon
            className={isGummy ? "h-5 w-5" : "h-4 w-4"}
            strokeWidth={isGummy ? 3 : undefined}
          />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
});
CheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

const RadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => {
  const isGummy = useIsGummy();
  return (
    <DropdownMenuPrimitive.RadioItem
      ref={ref}
      className={cn(
        "data-disabled:pointer-events-none data-disabled:opacity-50 relative flex cursor-default select-none items-center outline-hidden transition-colors",
        isGummy
          ? cn(GUMMY_ITEM, "pl-11")
          : cn(
              LEGACY_ITEM,
              "pl-8 focus:bg-accent hover:bg-mono-100 dark:hover:bg-mono-900 focus:text-accent-foreground"
            ),
        className
      )}
      {...props}
    >
      <span
        className={cn(
          "absolute left-3 flex items-center justify-center",
          isGummy ? "h-5 w-5" : "h-3.5 w-3.5"
        )}
      >
        <DropdownMenuPrimitive.ItemIndicator>
          <CircleStackIcon
            className={cn("fill-current", isGummy ? "h-3 w-3" : "h-2 w-2")}
          />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
});
RadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const Label = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => {
  const isGummy = useIsGummy();
  return (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={cn(
        isGummy
          ? "px-4 py-2.5 text-xs font-black uppercase tracking-widest text-mono-500 dark:text-mono-400"
          : "px-2 py-1.5 text-sm font-semibold dark:text-mono-100",
        inset && "pl-8",
        className
      )}
      {...props}
    />
  );
});
Label.displayName = DropdownMenuPrimitive.Label.displayName;

const Separator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => {
  const isGummy = useIsGummy();
  return (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      className={cn(
        "h-px",
        isGummy
          ? "-mx-1 my-2 bg-mono-200/70 dark:bg-mono-600/50"
          : "-mx-1 my-1 bg-mono-200 dark:bg-mono-700/30",
        className
      )}
      {...props}
    />
  );
});
Separator.displayName = DropdownMenuPrimitive.Separator.displayName;

const Arrow = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Arrow>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Arrow>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Arrow
    ref={ref}
    width={15}
    height={10}
    style={{
      marginTop: "0px"
    }}
    className={cn(
      "  fill-mono-50 drop-shadow-md dark:fill-mono-700/50",
      className
    )}
    {...props}
  />
));
Arrow.displayName = DropdownMenuPrimitive.Arrow.displayName;

const Shortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn("ml-auto text-xs tracking-widest opacity-60", className)}
      {...props}
    />
  );
};
Shortcut.displayName = "DropdownMenuShortcut";

export const DropdownMenu = {
  Root: Menu,
  withRoot,
  Arrow,
  Trigger,
  Content,
  Item,
  CheckboxItem,
  RadioItem,
  Label,
  Separator,
  Shortcut,
  Group,
  Portal,
  Sub,
  SubContent,
  SubTrigger,
  RadioGroup,
  useDropdownMenu
};
